/* eslint-disable no-undefined */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
  Box,
  Modal as MuiModal,
  IconButton,
  Tooltip
} from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import Button from "../../../components/Button";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import InputGroup from "../../../components/FormComponent/InputGroup";
import { TableSearch } from "../../../components/Table/TableActions";
import useStyles from "../bulkstyles";

const BulkSingleSubjectResultEntry = ({
  open,
  onClose,
  classId,
  className = "",
  subject = null,
  students = [],
  obtainableScores = [],
  gradingSystems = [],
  termId = "",
  termName = "",
  session = "",
  existingResults = [],
  classTestResults = [],
  classExamResults = [],
  onSave,
  isLoading = false,
  initialGradingSystemId = ""
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [selectedGradingSystem, setSelectedGradingSystem] = useState(initialGradingSystemId);
  const [searchQuery, setSearchQuery] = useState("");
  const [importCAScores, setImportCAScores] = useState(false);
  const [importExamScores, setImportExamScores] = useState(false);
  const [results, setResults] = useState({});
  const [scoreErrors, setScoreErrors] = useState({});
  const [gradingSystem, setGradingSystem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Get CA columns from obtainable scores
  const caColumns = obtainableScores?.[0]?.ca || [];
  const caCount = caColumns.length || 0;
  const fixedCols = 4; // CA_total + Exam + Total + Grade
  const colSpan = caCount + fixedCols + 1; // +1 for Student Name column

  // Function to get student ID
  const getStudentId = (student) => {
    // Handle direct student_id from result data
    if (student?.student_id) {
      return student.student_id;
    }

    // Handle nested student object
    return student?.student?.student_id ||
           student?.student?.id ||
           student?.id ||
           student?.student_id;
  };

  // Function to get student name
  const getStudentName = (student) => {
    if (student?.student) {
      return `${student.student.last_name || ""} ${student.student.first_name || ""}`.trim();
    }

    if (student?.first_name || student?.last_name) {
      return `${student.last_name || ""} ${student.first_name || ""}`.trim();
    }

    // fallbacks
    return student?.name || student?.student_name || "";
  };

  // Filter students based on search query
  const filteredStudents = useMemo(() => {
    if (!searchQuery?.trim()) {
      return Object?.keys(results);
    }

    const query = searchQuery?.toLowerCase();

    return Object.keys(results)?.filter(studentId => {
      const student = results[studentId];

      return student?.student_name?.toLowerCase()?.includes(query);
    });
  }, [results, searchQuery]);

  // Function to create an empty CA scores structure
  const createEmptyCAScores = () => {
    const caScores = {};
    if (caColumns?.length > 0) {
      caColumns?.forEach(ca => {
        caScores[ca?.ca_type] = "";
      });
    }

    return caScores;
  };

  // Function to parse existing results into a structured format
  const parseExistingResults = useCallback(() => {
    const parsedResults = {};

    if (!existingResults || existingResults.length === 0) {
      return parsedResults;
    }

    // Your data structure: each item has CA_scores, CA_total, exam_score, total, grade, student directly
    existingResults.forEach(resultEntry => {
      const studentId = resultEntry.student_id || getStudentId(resultEntry.student || resultEntry);

      if (studentId) {
        // Initialize CA scores
        const caScores = createEmptyCAScores();

        // Fill CA scores from existing results
        if (resultEntry?.CA_scores) {
          Object.keys(resultEntry.CA_scores).forEach(caKey => {
            const matchingCA = caColumns?.find(ca => ca?.ca_type?.toLowerCase()?.replace(/\s+/g, "") === caKey?.toLowerCase()?.replace(/\s+/g, "")
            );

            if (matchingCA) {
              caScores[matchingCA.ca_type] = resultEntry.CA_scores[caKey] || "";
            } else {
              // If no exact match, try to find similar
              const caType = caColumns?.find(ca => caKey?.includes(ca?.ca_type) || ca?.ca_type?.includes(caKey)
              )?.ca_type || caKey;
              caScores[caType] = resultEntry.CA_scores[caKey] || "";
            }
          });

          // Ensure all required CA columns are present
          caColumns?.forEach(ca => {
            if (!caScores[ca?.ca_type]) {
              caScores[ca?.ca_type] = "";
            }
          });
        }

        // Get values directly from resultEntry (not nested under scores)
        const caTotal = resultEntry?.CA_total ? Number(resultEntry.CA_total) :
          Object.values(caScores).reduce((sum, val) => sum + (Number(val) || 0), 0);

        const examScore = (resultEntry?.exam_score !== undefined && resultEntry?.exam_score !== null) ? resultEntry.exam_score : "";
        const totalScore = (resultEntry?.total !== undefined && resultEntry?.total !== null) ? Number(resultEntry.total) : (caTotal + (Number(examScore) || 0));
        const grade = resultEntry?.grade || "";
        const gradingSystemId = resultEntry?.grading_system?.id || selectedGradingSystem;

        parsedResults[studentId] = {
          student_id: studentId,
          student_name: getStudentName(resultEntry.student) || getStudentName(resultEntry),
          CA_scores: caScores,
          CA_total: caTotal,
          exam_score: examScore,
          total: totalScore,
          grade,
          grading_system_id: gradingSystemId
        };
      }
    });

    return parsedResults;
  }, [existingResults, caColumns, selectedGradingSystem]);

  // Function to initialize empty results for all students
  const initializeEmptyResults = useCallback(() => {
    const initialResults = {};

    students.forEach((student) => {
      const studentId = getStudentId(student);
      const studentName = getStudentName(student);

      // Get CA scores structure from obtainable scores
      const caScores = {};
      if (caColumns.length > 0) {
        caColumns?.forEach(ca => {
          caScores[ca?.ca_type] = "";
        });
      }

      initialResults[studentId] = {
        student_id: studentId,
        student_name: studentName,
        CA_scores: caScores,
        CA_total: 0,
        exam_score: "",
        total: 0,
        grade: "",
        grading_system_id: selectedGradingSystem
      };
    });

    return initialResults;
  }, [students, caColumns, selectedGradingSystem]);

  // Function to fill existing scores from API data
  const fillWithExistingScores = useCallback((emptyResults) => {
    if (!existingResults || existingResults.length === 0) {
      return emptyResults;
    }

    const parsedExistingResults = parseExistingResults();
    const updatedResults = { ...emptyResults };

    // Merge existing results with empty structure
    Object.keys(parsedExistingResults)?.forEach(studentId => {
      if (updatedResults[studentId]) {
        // Merge the existing scores with the empty structure
        updatedResults[studentId] = {
          ...updatedResults[studentId],
          ...parsedExistingResults[studentId],
          grading_system_id: parsedExistingResults[studentId]?.grading_system_id || selectedGradingSystem
        };
      }
    });

    return updatedResults;
  }, [existingResults, parseExistingResults, selectedGradingSystem]);

  // Function to import CA scores from classTestResults
  const importCAScoresFromTest = useCallback(() => {
    if (!classTestResults || classTestResults?.length === 0) {
      toast.info("No CA test results found to import");

      return;
    }

    setResults(prevResults => {
      const updatedResults = { ...prevResults };
      // let importedCount = 0;

      classTestResults.forEach(testItem => {
        const studentId = getStudentId(testItem.student);
        const caScores = testItem.ca_scores || {};

        if (studentId && updatedResults[studentId]) {
          // Create a new CA scores object by merging existing with imported
          const newCAScores = { ...updatedResults[studentId].CA_scores };

          // Import only the CA scores that match our CA columns
          Object.keys(caScores).forEach(caKey => {
            // Find matching CA column from obtainable scores
            const matchingCA = caColumns.find(ca => ca.ca_type.toLowerCase().replace(/\s+/g, "") === caKey.toLowerCase().replace(/\s+/g, "")
            );

            if (matchingCA && caScores[caKey]) {
              newCAScores[matchingCA.ca_type] = caScores[caKey];
              // importedCount++;
            } else {
              // If no exact match, try to find similar
              const caType = caColumns.find(ca => caKey.includes(ca.ca_type) || ca.ca_type.includes(caKey)
              )?.ca_type;

              if (caType && caScores[caKey]) {
                newCAScores[caType] = caScores[caKey];
                // importedCount++;
              }
            }
          });

          // Calculate CA total from imported scores
          const caTotal = Object.values(newCAScores).reduce((sum, val) => {
            const numVal = Number(val) || 0;

            return sum + numVal;
          }, 0);

          // Calculate total score
          const examScore = Number(updatedResults[studentId]?.exam_score) || 0;
          const totalScore = caTotal + examScore;

          // Calculate grade
          let grade = "";
          if (gradingSystem?.grading) {
            const gradeDetails = gradingSystem.grading.find(
              g => totalScore >= g.min && totalScore <= g.max
            );
            grade = gradeDetails?.grade || "";
          }

          // Update the student result with imported CA scores
          updatedResults[studentId] = {
            ...updatedResults[studentId],
            CA_scores: newCAScores,
            CA_total: caTotal,
            total: totalScore,
            grade,
            grading_system_id: selectedGradingSystem
          };
        }
      });

      return updatedResults;
    });
  }, [classTestResults, caColumns, gradingSystem, selectedGradingSystem]);

  const importExamScoresFromTest = useCallback(() => {
    if (!classExamResults || classExamResults?.length === 0) {
      toast.info("No Exam results found to import");

      return;
    }

    setResults(prevResults => {
      const updatedResults = { ...prevResults };
      let importedCount = 0;

      classExamResults.forEach(testItem => {
        const studentId = getStudentId(testItem.student);
        const examScore = testItem.score || testItem.exam_score || 0;

        if (studentId && updatedResults[studentId]) {
          const studentResult = updatedResults[studentId];
          const totalScore = (studentResult.CA_total || 0) + examScore;

          // Calculate grade if grading system exists
          let grade = studentResult.grade || "";
          if (gradingSystem && gradingSystem.grading) {
            const gradeDetails = gradingSystem.grading.find(
              g => totalScore >= g.min && totalScore <= g.max
            );
            grade = gradeDetails?.grade || "";
          }

          updatedResults[studentId] = {
            ...studentResult,
            exam_score: examScore,
            total: totalScore,
            grade,
            grading_system_id: selectedGradingSystem
          };
          importedCount++;
        }
      });

      if (importedCount > 0) {
        toast.success(`Imported ${importedCount} Exam scores`);
      } else {
        toast.info("No matching Exam scores found to import");
      }

      return updatedResults;
    });
  }, [classExamResults, gradingSystem, selectedGradingSystem]);

  // Initialize results when component opens or data changes
  useEffect(() => {
    if (open && students.length > 0 && subject && !hasInitialized) {
      // Initialize empty structure first
      const emptyResults = initializeEmptyResults();

      // Fill with existing results automatically
      const resultsWithExistingData = fillWithExistingScores(emptyResults);

      setResults(resultsWithExistingData);
      setHasInitialized(true);

      // Show toast if results were pre-filled
      // if (existingResults && existingResults?.length > 0) {
      //   const prefillCount = Object.keys(resultsWithExistingData)?.reduce((count, studentId) => {
      //     const studentResult = resultsWithExistingData[studentId];
      //     const hasPrefilledScores = studentResult?.CA_total > 0 ||
      //                              studentResult?.exam_score > 0 ||
      //                              Object.values(studentResult?.CA_scores || {})?.some(score => Number(score) > 0);

      //     return hasPrefilledScores ? count + 1 : count;
      //   }, 0);

      //   if (prefillCount > 0) {
      //     toast.success(`Pre-filled ${prefillCount} student results`);
      //   } else {
      //     toast.info("No existing results found to pre-fill");
      //   }
      // }
    }
  }, [open, students, subject, existingResults, initializeEmptyResults, fillWithExistingScores, hasInitialized]);

  // Import CA scores when checkbox is checked
  useEffect(() => {
    if (open && importCAScores && hasInitialized && results && Object.keys(results).length > 0) {
      setTimeout(() => {
        importCAScoresFromTest();
      }, 100);
    }
  }, [open, importCAScores, hasInitialized, importCAScoresFromTest, results]);

  // Import Exam scores when checkbox is checked
  useEffect(() => {
    if (open && importExamScores && hasInitialized && results && Object.keys(results).length > 0) {
      setTimeout(() => {
        importExamScoresFromTest();
      }, 100);
    }
  }, [open, importExamScores, hasInitialized, importExamScoresFromTest, results]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setResults({});
      setScoreErrors({});
      setImportCAScores(false);
      setImportExamScores(false);
      setSelectedGradingSystem(initialGradingSystemId);
      setSearchQuery("");
      setIsSubmitting(false);
      setHasInitialized(false);
    }
  }, [open, initialGradingSystemId]);

  // Update all grades when grading system changes
  const updateAllGrades = useCallback((system) => {
    if (!system) return;

    setResults(prevResults => {
      const updatedResults = { ...prevResults };

      Object.keys(updatedResults).forEach(studentId => {
        const studentResult = updatedResults[studentId];
        const total = studentResult.total || 0;

        const gradeDetails = system.grading.find(
          g => total >= g.min && total <= g.max
        );

        updatedResults[studentId] = {
          ...studentResult,
          grade: gradeDetails?.grade || "",
          grading_system_id: system.id
        };
      });

      return updatedResults;
    });
  }, []);

  // Set grading system when selected
  useEffect(() => {
    if (selectedGradingSystem && gradingSystems.length > 0) {
      const system = gradingSystems.find(g => g.id === selectedGradingSystem);
      setGradingSystem(system);

      // Update grades for all existing results
      updateAllGrades(system);
    }
  }, [selectedGradingSystem, gradingSystems, updateAllGrades]);

  // Handle CA score change
  const handleCAChange = (studentId, caType, value) => {
    const maxScore = obtainableScores?.[0]?.ca?.find(ca => ca.ca_type === caType)?.score || 100;
    const numericValue = Number(value) || 0;
    const errorKey = `${studentId}_${caType}`;

    // Validate max score
    if (numericValue > maxScore) {
      setScoreErrors(prev => ({
        ...prev,
        [errorKey]: `${caType} cannot exceed ${maxScore}`
      }));

      // Clear error after 3 seconds
      setTimeout(() => {
        setScoreErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];

          return newErrors;
        });
      }, 3000);

      return;
    } else {
      // Clear error if valid
      setScoreErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];

        return newErrors;
      });
    }

    setResults(prevResults => {
      const updatedResults = { ...prevResults };
      const studentResult = updatedResults[studentId];

      if (studentResult) {
        const updatedCA = {
          ...studentResult.CA_scores,
          [caType]: value
        };

        // Calculate CA total
        const caTotal = Object.values(updatedCA).reduce((sum, val) => {
          const numVal = Number(val) || 0;

          return sum + numVal;
        }, 0);

        // Calculate total score
        const examScore = Number(studentResult.exam_score) || 0;
        const totalScore = caTotal + examScore;

        // Calculate grade
        let grade = "";
        if (gradingSystem?.grading) {
          const gradeDetails = gradingSystem.grading.find(
            g => totalScore >= g.min && totalScore <= g.max
          );
          grade = gradeDetails?.grade || "";
        }

        updatedResults[studentId] = {
          ...studentResult,
          CA_scores: updatedCA,
          CA_total: caTotal,
          total: totalScore,
          grade,
          grading_system_id: selectedGradingSystem
        };
      }

      return updatedResults;
    });
  };

  // Handle exam score change
  const handleExamChange = (studentId, value) => {
    const maxExam = obtainableScores?.[0]?.examination || 100;
    const numericValue = Number(value) || 0;
    const errorKey = `${studentId}_exam`;

    // Validate max score
    if (numericValue > maxExam) {
      setScoreErrors(prev => ({
        ...prev,
        [errorKey]: `Exam score cannot exceed ${maxExam}`
      }));

      // Clear error after 3 seconds
      setTimeout(() => {
        setScoreErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];

          return newErrors;
        });
      }, 3000);

      return;
    } else {
      // Clear error if valid
      setScoreErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];

        return newErrors;
      });
    }

    setResults(prevResults => {
      const updatedResults = { ...prevResults };
      const studentResult = updatedResults[studentId];

      if (studentResult) {
        const examScore = numericValue;
        const caTotal = studentResult.CA_total || 0;
        const totalScore = caTotal + examScore;

        // Calculate grade
        let grade = "";
        if (gradingSystem?.grading) {
          const gradeDetails = gradingSystem.grading.find(
            g => totalScore >= g.min && totalScore <= g.max
          );
          grade = gradeDetails?.grade || "";
        }

        updatedResults[studentId] = {
          ...studentResult,
          exam_score: value,
          total: totalScore,
          grade,
          grading_system_id: selectedGradingSystem
        };
      }

      return updatedResults;
    });
  };

  // Handle grading system change
  const handleGradingSystemChange = (e) => {
    if (e.target.value === "create_new") {
      navigate("/report/grade-system");

      return;
    }
    setSelectedGradingSystem(e.target.value);
  };

  // Handle search student
  const handleSearchStudent = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle import CA scores checkbox change
  const handleImportCAScoresChange = (e) => {
    const isChecked = e.target.checked;
    setImportCAScores(isChecked);

    if (isChecked) {
      // Import CA scores when checked
      importCAScoresFromTest();
    } else {
      // When unchecked, revert to original pre-filled state
      const emptyResults = initializeEmptyResults();
      const resultsWithExistingData = fillWithExistingScores(emptyResults);
      setResults(resultsWithExistingData);
      if (importExamScores) {
        setTimeout(() => importExamScoresFromTest(), 0);
      }
    }
  };

  // Handle import Exam scores checkbox change
  const handleImportExamScoresChange = (e) => {
    const isChecked = e.target.checked;
    setImportExamScores(isChecked);

    if (isChecked) {
      // Import Exam scores when checked
      importExamScoresFromTest();
    } else {
      // When unchecked, revert to original pre-filled state
      const emptyResults = initializeEmptyResults();
      const resultsWithExistingData = fillWithExistingScores(emptyResults);
      setResults(resultsWithExistingData);
      if (importCAScores) {
        setTimeout(() => importCAScoresFromTest(), 0);
      }
    }
  };

  // Prepare data for submission in the required format
  const prepareSubmissionData = () => {
    if (!subject) {
      return null;
    }

    const subjectId = subject.id || subject.subject_id;

    // Format students data according to new structure
    const formattedStudents = [];

    Object.keys(results).forEach(studentId => {
      const studentResult = results[studentId];

      // Check if student has at least one score entered
      const hasCAScores = Object.values(studentResult.CA_scores || {}).some(score => {
        const numScore = Number(score);

        return !isNaN(numScore) && numScore > 0;
      });

      const hasExamScore = Number(studentResult.exam_score) > 0;

      if (hasCAScores || hasExamScore) {
        // Convert CA scores to numbers
        const formattedCAScores = {};
        Object.keys(studentResult.CA_scores || {}).forEach(caType => {
          const score = studentResult.CA_scores[caType];
          formattedCAScores[caType] = score ? Number(score) : 0;
        });

        formattedStudents.push({
          student_id: studentId,
          subjects: [
            {
              subject_id: subjectId,
              CA_total: studentResult.CA_total || 0,
              CA_scores: formattedCAScores,
              exam_score: Number(studentResult.exam_score) || 0,
              total: studentResult.total || 0,
              grade: studentResult.grade || ""
            }
          ]
        });
      }
    });

    if (formattedStudents.length === 0) {
      return null;
    }

    return {
      class_id: classId,
      term_id: termId,
      grading_system_id: selectedGradingSystem,
      grade_type: "manual",
      subject_id: subjectId,
      students: formattedStudents
    };
  };

  // Handle submit - with proper loading state management
  const handleSubmit = async () => {
    try {
      if (!subject) {
        toast.error("No subject selected");

        return;
      }

      const submissionData = prepareSubmissionData();

      if (!submissionData) {
        toast.error("No scores entered to save");

        return;
      }

      // Set submitting state to true
      setIsSubmitting(true);

      // Call the onSave function with formatted data
      if (onSave && typeof onSave === "function") {
        await onSave(submissionData);
      } else {
        toast.error("Save function is not available");
      }
    } catch (error) {
      toast.error("Failed to save results");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    let totalEntries = 0;
    let filledEntries = 0;
    let prefilledEntries = 0;

    Object.keys(results).forEach(studentId => {
      totalEntries++;
      const studentResult = results[studentId];

      // Check if student has at least one score entered
      const hasCAScores = Object.values(studentResult?.CA_scores || {}).some(score => {
        const numScore = Number(score);

        return !isNaN(numScore) && numScore > 0;
      });

      const hasExamScore = Number(studentResult?.exam_score) > 0;

      if (hasCAScores || hasExamScore) {
        filledEntries++;
      }

      // Check if this entry was pre-filled from existing results
      const hasPrefilledScores = Object.values(studentResult?.CA_scores || {}).some(score => {
        const numScore = Number(score);

        return !isNaN(numScore) && numScore > 0;
      }) || Number(studentResult?.exam_score) > 0 || studentResult?.CA_total > 0;

      if (hasPrefilledScores) {
        prefilledEntries++;
      }
    });

    return { totalEntries, filledEntries, prefilledEntries };
  };

  const stats = calculateStats();

  // Combine loading states: parent's isLoading or our internal isSubmitting
  const isSaving = isLoading || isSubmitting;

  if (!subject) {
    return (
      <MuiModal open={open} onClose={onClose}>
        <Box className={classes.bulkResultModalContent}>
          <div className={classes.bulkModalHeader}>
            <h2 className={classes.bulkModalTitle}>Bulk Add Result</h2>
            <IconButton onClick={onClose} className={classes.closeButton}>
              <Icon icon="mdi:close" width={24} height={24} />
            </IconButton>
          </div>
          <div className={classes.bulkModalBody}>
            <div className={classes.noSubjectMessage}>
              <Icon icon="mdi:alert-circle-outline" width={48} height={48} />
              <p>No subject selected. Please select a subject first.</p>
            </div>
          </div>
        </Box>
      </MuiModal>
    );
  }

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      className={classes.bulkResultModal}
      disableEscapeKeyDown={isSaving}
      disableBackdropClick={isSaving}
    >
      <Box className={classes.bulkResultModalContent}>
        <div className={classes.bulkModalHeader}>
          <div className={classes.headerTopSection}>
            <div className={classes.headerInfo}>
              <h2 className={classes.bulkModalTitle}>
                Add Student Result - {subject.name || subject.subject_name}
              </h2>
              <div className={classes.courseInfo}>
                <span className={classes.infoItem}>
                  <strong>Class:</strong> {className || classId}
                </span>
                <span className={classes.infoItem}>
                  <strong>Term:</strong> {termName || "Not Selected"}
                </span>
                <span className={classes.infoItem}>
                  <strong>Session:</strong> {session || "Not Selected"}
                </span>
                <span className={classes.infoItem}>
                  <strong>Students:</strong> {Object.keys(results).length || 0}
                </span>
                <span className={classes.infoItem}>
                  <strong>Pre-filled:</strong> {stats.prefilledEntries} students
                </span>
              </div>
            </div>
            <IconButton onClick={onClose} className={classes.closeButton}>
              <Icon icon="mdi:close" width={24} height={24} />
            </IconButton>
          </div>
        </div>

        <div className={classes.bulkModalBody}>
          {/* Controls Section */}
          <div className={classes.bulkControls}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <SelectGroup
                  label="Grading System"
                  required
                  children={
                    <select
                      value={selectedGradingSystem}
                      onChange={handleGradingSystemChange}
                      disabled={isSaving}
                    >
                      <option value="">--- Select Grading System ---</option>
                      {gradingSystems.map(system => (
                        <option key={system.id} value={system.id}>
                          {system.title}
                        </option>
                      ))}
                      <option value="create_new" style={{ color: "#155EEF", fontWeight: 500 }}>
                        + Create Grading System
                      </option>
                    </select>
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <div className={classes.searchContainer}>
                  <TableSearch
                    placeholder="Search Student..."
                    searchTableFunc={handleSearchStudent}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={4}>
                <div className={classes.checkboxContainer}>
                  <label className={classes.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={importCAScores}
                      onChange={handleImportCAScoresChange}
                      disabled={!classTestResults || classTestResults.length === 0}
                    />
                    <span>Import CA Scores from Tests</span>
                  </label>
                  {classTestResults && classTestResults.length > 0 ? (
                    <div className={classes.checkboxHint}>
                      <Icon icon="mdi:information" width={14} height={14} />
                      <small>Import {classTestResults.length} CA test results</small>
                    </div>
                  ) : (
                    <div className={classes.checkboxHint}>
                      <Icon icon="mdi:alert" width={14} height={14} />
                      <small>No CA test results found</small>
                    </div>
                  )}
                </div>
                <div className={classes.checkboxContainer} style={{ marginTop: "10px" }}>
                  <label className={classes.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={importExamScores}
                      onChange={handleImportExamScoresChange}
                      disabled={!classExamResults || classExamResults.length === 0}
                    />
                    <span>Import Exam Scores from Exams</span>
                  </label>
                  {classExamResults && classExamResults.length > 0 ? (
                    <div className={classes.checkboxHint}>
                      <Icon icon="mdi:information" width={14} height={14} />
                      <small>Import {classExamResults.length} Exam results</small>
                    </div>
                  ) : (
                    <div className={classes.checkboxHint}>
                      <Icon icon="mdi:alert" width={14} height={14} />
                      <small>No Exam results found</small>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>

          {/* Stats Bar */}
          <div className={classes.statsBar}>
            <div className={classes.statItem}>
              <Icon icon="mdi:account-check" width={16} height={16} style={{ color: "#4caf50" }} />
              <span>Pre-filled: {stats.prefilledEntries}</span>
            </div>
          </div>

          {/* Results Table */}
          <div className={classes.bulkTableContainer}>
            <TableContainer
              component={Paper}
              className={classes.bulkTable}
              style={{ maxHeight: "500px", overflow: "auto" }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  {/* First row: Subject header spanning all columns - Sticky */}
                  <TableRow>
                    <TableCell
                      className={classes.bulkHeaderCell}
                      colSpan={colSpan}
                      align="center"
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        position: "sticky",
                        top: 0,
                        zIndex: 6,
                        backgroundColor: "#f5f5f5"
                      }}
                    >
                      {subject.name || subject.subject_name}
                    </TableCell>
                  </TableRow>

                  {/* Second row: Column headers - Sticky */}
                  <TableRow>
                    {/* Student Name Column - Sticky */}
                    <TableCell
                      className={classes.bulkHeaderCell}
                      style={{
                        minWidth: "180px",
                        position: "sticky",
                        left: 0,
                        top: "40px",
                        zIndex: 5,
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0"
                      }}
                    >
                      Student Name
                    </TableCell>

                    {/* CA Score Columns - Sticky */}
                    {caColumns.map((ca, caIndex) => (
                      <TableCell
                        key={ca.ca_type}
                        className={classes.bulkHeaderCell}
                        align="center"
                        style={{
                          minWidth: "70px",
                          fontSize: "12px",
                          padding: "6px 6px",
                          position: "sticky",
                          top: "40px",
                          left: caIndex === 0 ? "180px" : null,
                          zIndex: 4,
                          backgroundColor: "#f5f5f5"
                        }}
                      >
                        <div className={classes.columnHeader}>
                          <div className={classes.caType}>
                            {ca.ca_type} <br />
                            <small style={{ fontSize: "10px" }}>(Max: {ca.score})</small>
                          </div>
                        </div>
                      </TableCell>
                    ))}

                    {/* Total CA Column - Sticky */}
                    <TableCell
                      className={classes.bulkHeaderCell}
                      align="center"
                      style={{
                        minWidth: "60px",
                        fontSize: "12px",
                        position: "sticky",
                        top: "40px",
                        left: `calc(180px + ${caColumns.length * 70}px)`,
                        zIndex: 4,
                        backgroundColor: "#f5f5f5"
                      }}
                    >
                      <div className={classes.columnHeader}>
                        <div className={classes.caType}>Total CA</div>
                      </div>
                    </TableCell>

                    {/* Exam Column - Sticky */}
                    <TableCell
                      className={classes.bulkHeaderCell}
                      align="center"
                      style={{
                        minWidth: "70px",
                        fontSize: "12px",
                        padding: "6px 6px",
                        position: "sticky",
                        top: "40px",
                        left: `calc(180px + ${caColumns.length * 70 + 60}px)`,
                        zIndex: 4,
                        backgroundColor: "#f5f5f5"
                      }}
                    >
                      <div className={classes.columnHeader}>
                          Exam <br />
                        <small style={{ fontSize: "10px" }}>(Max: {obtainableScores?.[0]?.examination || 100})</small>
                      </div>
                    </TableCell>

                    {/* Total Column - Sticky */}
                    <TableCell
                      className={classes.bulkHeaderCell}
                      align="center"
                      style={{
                        minWidth: "60px",
                        fontSize: "12px",
                        position: "sticky",
                        top: "40px",
                        left: `calc(180px + ${caColumns.length * 70 + 60 + 70}px)`,
                        zIndex: 4,
                        backgroundColor: "#f5f5f5"
                      }}
                    >
                      <div className={classes.columnHeader}>
                        <div className={classes.caType}>Total</div>
                      </div>
                    </TableCell>

                    {/* Grade Column - Sticky */}
                    <TableCell
                      className={classes.bulkHeaderCell}
                      align="center"
                      style={{
                        minWidth: "50px",
                        fontSize: "12px",
                        position: "sticky",
                        top: "40px",
                        left: `calc(180px + ${caColumns.length * 70 + 60 + 70 + 60}px)`,
                        zIndex: 4,
                        backgroundColor: "#f5f5f5"
                      }}
                    >
                      <div className={classes.columnHeader}>
                        <div className={classes.caType}>Grade</div>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredStudents?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={colSpan}
                        align="center"
                        style={{ padding: "40px" }}
                      >
                        <div className={classes.noResults}>
                          <Icon icon="mdi:account-search" width={48} height={48} style={{ color: "#ccc" }} />
                          <p>{searchQuery ? `No students found matching "${searchQuery}"` : "No students to display"}</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((studentId, rowIndex) => {
                      const studentResult = results[studentId];
                      const hasExistingData = studentResult?.CA_total > 0 ||
                                             studentResult?.exam_score > 0 ||
                                             Object.values(studentResult?.CA_scores || {})?.some(score => {
                                               const numScore = Number(score);

                                               return !isNaN(numScore) && numScore > 0;
                                             });

                      return (
                        <TableRow
                          key={studentId}
                          className={rowIndex % 2 === 0 ? classes.bulkRowEven : classes.bulkRowOdd}
                        >
                          {/* Student Name - Sticky */}
                          <TableCell
                            className={classes.studentNameCell}
                            style={{
                              position: "sticky",
                              left: 0,
                              zIndex: 2,
                              backgroundColor: rowIndex % 2 === 0 ? "#fff" : "#f9f9f9",
                              minWidth: "180px",
                              fontSize: "13px",
                              borderRight: "1px solid #e0e0e0"
                            }}
                          >
                            {studentResult?.student_name || "Unknown Student"}
                          </TableCell>

                          {/* CA Score Inputs */}
                          {caColumns.map(ca => {
                            const errorKey = `${studentId}_${ca.ca_type}`;
                            const hasError = scoreErrors[errorKey];
                            const inputId = `ca_${studentId}_${ca.ca_type}`;
                            const caScore = studentResult?.CA_scores?.[ca.ca_type];

                            return (
                              <TableCell
                                key={`${studentId}_${ca.ca_type}`}
                                align="center"
                                className={classes.bulkCell}
                                style={{
                                  minWidth: "70px",
                                  backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                                }}
                              >
                                <Tooltip
                                  title={hasError || ""}
                                  arrow
                                  placement="top"
                                  open={!!hasError}
                                  classes={{
                                    tooltip: classes.errorTooltip,
                                    arrow: classes.errorTooltipArrow
                                  }}
                                >
                                  <div className={classes.inputContainer}>
                                    <InputGroup
                                      inputName={inputId}
                                      inputType="number"
                                      value={caScore ?? ""}
                                      onChange={(e) => handleCAChange(studentId, ca.ca_type, e.target.value)}
                                      placeHolder="0"
                                      disabled={isSaving}
                                    />
                                  </div>
                                </Tooltip>
                              </TableCell>
                            );
                          })}

                          {/* Total CA Score (readonly) */}
                          <TableCell
                            align="center"
                            className={classes.totalCell}
                            style={{
                              minWidth: "60px",
                              backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                            }}
                          >
                            <div className={classes.totalDisplay}>
                              {studentResult?.CA_total || 0}
                            </div>
                          </TableCell>

                          {/* Exam Score Input */}
                          <TableCell
                            align="center"
                            className={classes.bulkCell}
                            style={{
                              minWidth: "70px",
                              backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                            }}
                          >
                            <Tooltip
                              title={scoreErrors[`${studentId}_exam`] || ""}
                              arrow
                              placement="top"
                              open={!!scoreErrors[`${studentId}_exam`]}
                              classes={{
                                tooltip: classes.errorTooltip,
                                arrow: classes.errorTooltipArrow
                              }}
                            >
                              <div className={classes.inputContainer}>
                                <InputGroup
                                  inputName={`exam_${studentId}`}
                                  inputType="number"
                                  value={studentResult?.exam_score ?? ""}
                                  onChange={(e) => handleExamChange(studentId, e.target.value)}
                                  placeHolder="0"
                                  disabled={isSaving}
                                />
                              </div>
                            </Tooltip>
                          </TableCell>

                          {/* Total Score (readonly) */}
                          <TableCell
                            align="center"
                            className={classes.totalCell}
                            style={{
                              minWidth: "60px",
                              backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                            }}
                          >
                            <div className={classes.totalDisplay}>
                              {studentResult?.total || 0}
                            </div>
                          </TableCell>

                          {/* Grade (readonly) */}
                          <TableCell
                            align="center"
                            className={classes.gradeCell}
                            style={{
                              minWidth: "50px",
                              backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                            }}
                          >
                            <div className={classes.gradeDisplay}>
                              {studentResult?.grade || "-"}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Footer */}
          <div className={classes.bulkModalFooter}>
            <div className={classes.bulkStats}>
              <div className={classes.statsGrid}>
                <div className={classes.statItem}>
                  <Icon icon="mdi:account-group" width={16} height={16} />
                  <span>Students: {filteredStudents.length}/{students.length}</span>
                </div>
                <div className={classes.statItem}>
                  <Icon icon="mdi:check-circle" width={16} height={16} />
                  <span>Filled: {stats.filledEntries}</span>
                </div>
                <div className={classes.statItem}>
                  <Icon icon="mdi:clock-outline" width={16} height={16} />
                  <span>Pending: {stats.totalEntries - stats.filledEntries}</span>
                </div>
              </div>
            </div>

            <div className={classes.actionButtons}>
              <Button
                variant="dangeroutline"
                buttonSize="medium"
                color="btndanger"
                buttonName="Cancel"
                onClick={onClose}
                disabled={isSaving}
                className={classes.cancelButton}
              />
              <Button
                variant="primary"
                buttonSize="medium"
                color="btndefault"
                buttonName={isSaving ? "Saving Results..." : "Save All Results"}
                onClick={handleSubmit}
                isLoading={isSaving}
                disabled={stats.filledEntries === 0 || isSaving}
              />
            </div>
          </div>
        </div>
      </Box>
    </MuiModal>
  );
};

export default BulkSingleSubjectResultEntry;
